const museumEventTypes = import('@theme/events').catch(() => null);

class MuseumProduct extends HTMLElement {
  connectedCallback() {
    if (this._active) return;
    this._active = true;
    this.abort = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-product-config]').textContent);
    this.strings = this.config.strings;
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-variant-input]');
    this.quantity = this.querySelector('[data-quantity]');
    this.mainAdd = this.querySelector('[data-main-add]');
    this.sticky = this.querySelector('[data-sticky]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
    this.mobile = matchMedia('(max-width: 749px)');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.reduced.matches;
    this.galleryVisible = true;
    this.hovered = false;
    this.keyboardFocused = false;
    this.busy = false;
    this.changed = false;
    const initial = this.config.variants.find(v => String(v.id) === String(this.config.selectedId)) || this.config.variants.find(v => v.available) || this.config.variants[0];
    this.choices = [...initial.options];
    const sourceOptions = this.querySelector('[data-main-options]');
    if (sourceOptions) {
      sourceOptions.hidden = false;
      const copy = sourceOptions.cloneNode(true);
      copy.removeAttribute('data-main-options');
      this.querySelector('[data-sticky-options]').append(...copy.children);
    }
    this.querySelector('[data-fallback]').hidden = true;
    this.listen(this, 'click', event => this.onClick(event));
    this.listen(this.form, 'submit', event => this.submit(event));
    this.listen(document, 'visibilitychange', () => this.schedule());
    this.listen(this.mobile, 'change', () => this.updateSticky());
    this.listen(this.reduced, 'change', () => { this.paused = this.reduced.matches; this.updatePlay(); this.schedule(); });
    this.listen(document, 'focusin', () => this.updateSticky());
    this.listen(document, 'focusout', () => requestAnimationFrame(() => this.updateSticky()));
    this.updateVariants(false);
    this.setupGallery();
    this.setupSticky();
    this.updatePlay();
    this.schedule();
  }

  disconnectedCallback() {
    this._active = false;
    this.abort?.abort();
    this.cartRequest?.abort();
    clearTimeout(this.timer);
    clearTimeout(this.addedTimer);
    cancelAnimationFrame(this.stickyFrame);
    this.galleryObserver?.disconnect();
    this.stickyResize?.disconnect();
    this.dialog?.close();
    document.body.style.removeProperty('--ma-sticky-height');
  }

  listen(target, type, handler, options = {}) {
    target?.addEventListener(type, handler, { ...options, signal: this.abort.signal });
  }

  onClick(event) {
    const option = event.target.closest('[data-option-value]');
    if (option && !option.disabled && !this.busy) {
      const changedIndex = Number(option.dataset.optionIndex);
      this.choices[changedIndex] = option.dataset.optionValue;
      for (let index = changedIndex + 1; index < this.choices.length; index++) {
        if (this.choices[index] !== null && !this.config.variants.some(v => v.available && v.options.every((value, i) => i > index || this.choices[i] === null || value === this.choices[i]))) this.choices[index] = null;
      }
      this.changed = true;
      this.clearErrors();
      this.updateVariants(true);
      return;
    }
    const thumb = event.target.closest('[data-thumbnail]');
    if (thumb) this.showImage(Number(thumb.dataset.thumbnail), true);
    if (event.target.closest('[data-next]')) this.showImage((this.index + 1) % this.slides.length, true);
    if (event.target.closest('[data-previous]')) this.showImage((this.index - 1 + this.slides.length) % this.slides.length, true);
    if (event.target.closest('[data-play]')) { this.paused = !this.paused; this.updatePlay(); this.schedule(); }
    if (event.target.closest('[data-zoom-open]')) this.openZoom();
    if (event.target.closest('[data-zoom-close]')) this.dialog?.close();
  }

  updateVariants(updateUrl) {
    const variants = this.config.variants;
    const complete = this.choices.every(value => value !== null);
    this.variant = complete ? variants.find(v => v.options.every((value, index) => value === this.choices[index])) : null;
    const display = this.variant || variants.find(v => v.available) || variants[0];
    this.variantInput.value = this.variant ? String(this.variant.id) : '';
    this.quantity.value = this.variant?.minimum || 1;
    this.querySelectorAll('[data-option-value]').forEach(button => {
      const index = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue;
      const selected = this.choices[index] === value;
      const matches = variants.filter(v => v.options[index] === value && v.options.every((item, i) => i >= index || this.choices[i] === null || this.choices[i] === item));
      button.disabled = this.busy || !matches.some(v => v.available);
      button.setAttribute('aria-pressed', String(selected));
      button.toggleAttribute('data-selected-soldout', selected && !matches.some(v => v.available));
    });
    this.querySelectorAll('fieldset[data-option-index]').forEach(fieldset => {
      fieldset.querySelector('[data-option-selected]').textContent = this.choices[Number(fieldset.dataset.optionIndex)] || '';
    });
    const price = display.price > 0 ? display.money : this.strings.priceSoon;
    this.querySelector('[data-price]').textContent = price;
    this.querySelector('[data-sticky-price]').textContent = price;
    const compare = this.querySelector('[data-compare]');
    compare.hidden = display.price <= 0 || display.compare <= display.price;
    compare.textContent = display.compareMoney;
    const available = this.variant ? this.variant.available : this.config.available;
    this.querySelector('[data-stock]').textContent = available ? this.strings.available : this.strings.soldOut;
    const note = this.querySelector('[data-selection-note]');
    note.textContent = this.variant && !this.config.defaultOnly ? this.variant.options.join(' / ').toUpperCase() : this.config.available && !this.config.defaultOnly ? this.strings.choose : '';
    this.updateButtons();
    if (updateUrl && this.variant) {
      const url = new URL(location.href);
      url.searchParams.set('variant', String(this.variant.id));
      history.replaceState(history.state, '', url);
      if (this.variant.imageId) {
        const imageIndex = this.slides.findIndex(slide => String(slide.dataset.imageId) === String(this.variant.imageId));
        if (imageIndex >= 0) this.showImage(imageIndex, true);
      }
    }
  }

  updateButtons() {
    let text = this.strings.add;
    let disabled = false;
    if (this.busy) { text = this.strings.adding; disabled = true; }
    else if (!this.config.available) { text = this.strings.soldOut; disabled = true; }
    else if (!this.variant) { text = this.choices.every(v => v !== null) ? this.strings.unavailable : this.strings.choose; disabled = true; }
    else if (!this.variant.available) { text = this.strings.soldOut; disabled = true; }
    else if (this.variant.price <= 0) { text = this.strings.priceSoon; disabled = true; }
    this.querySelectorAll('[data-main-add],[data-sticky-add]').forEach(button => {
      button.disabled = disabled;
      button.toggleAttribute('data-busy', this.busy);
      button.setAttribute('aria-busy', String(this.busy));
      button.querySelector('[data-add-text]').textContent = text;
    });
  }

  setupGallery() {
    this.dialog = this.querySelector('[data-zoom]');
    const gallery = this.querySelector('.ma-product__gallery');
    this.querySelector('[data-gallery-controls]')?.removeAttribute('hidden');
    this.listen(gallery, 'pointerenter', event => { if (event.pointerType === 'mouse') { this.hovered = true; this.schedule(); } });
    this.listen(gallery, 'pointerleave', () => { this.hovered = false; this.schedule(); });
    this.listen(gallery, 'focusin', event => { this.keyboardFocused = event.target.matches(':focus-visible'); this.schedule(); });
    this.listen(gallery, 'focusout', event => { if (!gallery.contains(event.relatedTarget)) { this.keyboardFocused = false; this.schedule(); } });
    this.listen(gallery, 'keydown', event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.showImage((this.index + (event.key === 'ArrowRight' ? 1 : -1) + this.slides.length) % this.slides.length, true);
    });
    const stage = this.querySelector('[data-stage]');
    this.listen(stage, 'pointerdown', event => { if (event.pointerType !== 'mouse') this.pointerStart = { x:event.clientX, y:event.clientY }; });
    this.listen(stage, 'pointerup', event => {
      if (!this.pointerStart) return;
      const dx = event.clientX - this.pointerStart.x;
      const dy = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4 && this.slides.length > 1) this.showImage((this.index + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length, true);
    });
    this.listen(stage, 'pointercancel', () => { this.pointerStart = null; });
    this.listen(this.dialog, 'close', () => this.schedule());
    if ('IntersectionObserver' in window) {
      this.galleryObserver = new IntersectionObserver(entries => { this.galleryVisible = entries[0].isIntersecting; this.schedule(); });
      this.galleryObserver.observe(stage);
    }
    this.preloadNext();
  }

  async showImage(index, manual = false) {
    if (!this.slides.length || !this.slides[index]) return;
    clearTimeout(this.timer);
    const token = this.imageToken = (this.imageToken || 0) + 1;
    const image = this.slides[index].querySelector('img');
    image.loading = 'eager';
    try {
      if (image.decode) await image.decode();
      if (!image.naturalWidth) throw new Error('Image did not load');
    } catch {
      if (manual) this.querySelector('[data-live]').textContent = this.strings.imageError;
      this.schedule();
      return;
    }
    if (token !== this.imageToken || !this.isConnected) return;
    this.index = index;
    this.slides.forEach((slide, i) => { slide.hidden = i !== index; });
    this.querySelectorAll('[data-thumbnail]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.thumbnail) === index)));
    const counter = this.querySelector('[data-counter]');
    if (counter) counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.slides.length).padStart(2, '0')}`;
    if (this.dialog?.open) this.copyZoomImage();
    this.preloadNext();
    this.schedule();
  }

  preloadNext() {
    if (this.slides.length < 2) return;
    const image = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
    image.loading = 'eager';
    image.decode?.().catch(() => {});
  }

  schedule() {
    clearTimeout(this.timer);
    if (!this._active || this.paused || this.hovered || this.keyboardFocused || !this.galleryVisible || document.hidden || this.dialog?.open || this.slides.length < 2) return;
    this.timer = setTimeout(() => this.showImage((this.index + 1) % this.slides.length), 4000);
  }

  updatePlay() {
    const button = this.querySelector('[data-play]');
    if (!button) return;
    button.setAttribute('aria-label', this.paused ? this.strings.play : this.strings.pause);
    button.setAttribute('aria-pressed', String(this.paused));
    button.querySelector('[data-play-path]').setAttribute('d', this.paused ? 'M7 5v14l11-7-11-7Z' : 'M8 5v14M16 5v14');
  }

  copyZoomImage() {
    const source = this.slides[this.index]?.querySelector('img');
    const target = this.querySelector('[data-zoom-image]');
    if (!source || !target) return;
    target.src = source.currentSrc || source.src;
    target.alt = source.alt;
  }

  openZoom() {
    if (!this.dialog || !this.slides.length) return;
    this.copyZoomImage();
    this.dialog.showModal();
    this.schedule();
  }

  setupSticky() {
    const check = () => {
      if (this.stickyFrame) return;
      this.stickyFrame = requestAnimationFrame(() => {
        this.stickyFrame = 0;
        this.updateSticky();
      });
    };
    this.listen(window, 'scroll', check, { passive:true });
    this.listen(window, 'resize', check, { passive:true });
    if ('ResizeObserver' in window) {
      this.stickyResize = new ResizeObserver(() => this.reserveStickySpace());
      this.stickyResize.observe(this.sticky);
    }
    this.listen(window.visualViewport, 'resize', () => this.updateSticky());
    this.updateSticky();
  }

  updateSticky() {
    const active = document.activeElement;
    const keyboard = active?.matches('input:not([type=hidden]),textarea,select,[contenteditable=true]');
    const drawerOpen = [...document.querySelectorAll('dialog[open]')].some(dialog => dialog !== this.dialog);
    const passedMainButton = this.mobile.matches && this.mainAdd.getBoundingClientRect().bottom <= 0;
    this.sticky.hidden = !passedMainButton || Boolean(keyboard) || drawerOpen;
    this.reserveStickySpace();
  }

  reserveStickySpace() {
    const height = this.mobile.matches && !this.sticky.hidden ? this.sticky.getBoundingClientRect().height : 0;
    this.style.setProperty('--ma-sticky-height', `${height}px`);
  }

  clearErrors() {
    this.querySelectorAll('[data-error],[data-sticky-error]').forEach(error => { error.hidden = true; error.textContent = ''; });
  }

  showError(message) {
    this.querySelectorAll('[data-error],[data-sticky-error]').forEach(error => { error.textContent = message; error.hidden = false; });
    this.querySelector('[data-live]').textContent = message;
  }

  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.variant?.available || this.variant.price <= 0) return;
    const eventTypes = await museumEventTypes;
    if (this.busy || !this.variant?.available || this.variant.price <= 0) return;
    if (!eventTypes?.CartAddEvent) {
      HTMLFormElement.prototype.submit.call(this.form);
      return;
    }
    const variant = this.variant;
    const quantity = Number(this.quantity.value) || 1;
    this.busy = true;
    this.clearErrors();
    this.updateVariants(false);
    const formData = new FormData(this.form);
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(node => node.dataset.sectionId).filter(Boolean))].slice(0, 5);
    if (sections.length) formData.set('sections', sections.join(','));
    formData.set('sections_url', location.pathname + location.search);
    let succeeded = false;
    try {
      const endpoint = this.dataset.cartUrl.endsWith('.js') ? this.dataset.cartUrl : this.dataset.cartUrl + '.js';
      this.cartRequest = new AbortController();
      this.cartTimeout = setTimeout(() => this.cartRequest.abort(), 20000);
      const response = await fetch(endpoint, { method:'POST', body:formData, headers:{ Accept:'application/json' }, signal:this.cartRequest.signal });
      const data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || this.strings.error;
        this.showError(typeof message === 'string' ? message : this.strings.error);
        if (eventTypes.CartErrorEvent) window.dispatchEvent(new eventTypes.CartErrorEvent(this.form.id, typeof message === 'string' ? message : this.strings.error));
        return;
      }
      succeeded = true;
      this.dispatchEvent(new eventTypes.CartAddEvent({}, String(variant.id), { source:'product-form-component', itemCount:quantity, productId:this.dataset.productId, sections:data.sections }));
      this.querySelector('[data-live]').textContent = this.strings.added;
      if (!document.querySelector('cart-drawer-component')) location.assign(this.dataset.cartPage);
    } catch {
      this.showError(this.strings.error);
    } finally {
      clearTimeout(this.cartTimeout);
      this.busy = false;
      this.updateVariants(false);
      if (succeeded) {
        this.querySelectorAll('[data-add-text]').forEach(label => { label.textContent = this.strings.added; });
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => this.isConnected && this.updateButtons(), 2200);
        this.updateSticky();
      }
    }
  }
}

if (!customElements.get('ma-product')) customElements.define('ma-product', MuseumProduct);